<script setup lang="ts">
import { computed, ref, onBeforeMount } from "vue";
import { HomeService } from "~/services/HomeService";
import { store } from "~/store";
import {
  VITE_BANNER_CAROUSEL_V2,
  VITE_BANNER_CAROUSEL_SECONDARY,
} from "~/config/constants.js";
import FindByBrand from "@/components/home/FindByBrand.vue";
import HomeBannerGallery from "@/components/home/HomeBannerGallery.vue";
import PaymentSection from "@/components/home/PaymentSection.vue";
import SavePurchaseSection from "@/components/home/SavePurchaseSection.vue";
import PromotionSection from "@/components/home/PromotionSection.vue";
import PromotionGallery from "@/components/home/PromotionGallery.vue";
import CarouselImages from "@/components/ui/CarouselImages.vue";
import { useHomeSeo } from "~/composables/useHomeSeo";

interface HomeSlide {
  id: number;
  name: string;
  image: string;
  action_url: string;
  sm_image_url: string;
}

type HeroPreloadLink = {
  rel: "preload";
  as: "image";
  href: string;
  media: string;
  fetchpriority: "high";
};

definePageMeta({
  name: "home",
});

// state
const slides = useState<HomeSlide[]>("home.slides", () => []);
const secondarySlides = useState<HomeSlide[]>("home.secondarySlides", () => []);
const loadingBanners = ref(false);
const routeHydrated = useRouteHydrated();
useHomeSeo();

const heroPreloadLinks = computed<HeroPreloadLink[]>(() => {
  const firstSlide = slides.value[0];
  if (!firstSlide) {
    return [];
  }

  const links: HeroPreloadLink[] = [
    {
      rel: "preload",
      as: "image",
      href: firstSlide.image,
      media: "(min-width: 992px)",
      fetchpriority: "high",
    },
  ];

  if (firstSlide.sm_image_url) {
    links.push({
      rel: "preload",
      as: "image",
      href: firstSlide.sm_image_url,
      media: "(max-width: 991.98px)",
      fetchpriority: "high",
    });
  }

  return links;
});

useHead(() => ({
  link: heroPreloadLinks.value,
}));

// lifecycle (equivalente a `created`)
onBeforeMount(() => {
  store.commit("setCanCloseMenu", false);
});

const homeService = new HomeService();
loadingBanners.value = true;

const init = async () => {
  try {
    if (!routeHydrated.value) {
      slides.value = await homeService.getBanners(VITE_BANNER_CAROUSEL_V2);
      secondarySlides.value =
        (await homeService.getBanners(VITE_BANNER_CAROUSEL_SECONDARY)) || [];
    }
  } catch (error) {
    slides.value = [];
    secondarySlides.value = [];
    console.error("Error loading home banners", error);
  } finally {
    loadingBanners.value = false;
  }
};

if (import.meta.server) {
  await init();
} else {
  init(); // en cliente mostramos pantallas de carga
}
</script>

<template>
  <div>
    <section
      class="col-12 col-lg-9 col-xl-10 offset-lg-3 offset-xl-2 mb-swiper"
    >
      <CarouselImages
        :slides="slides"
        :secondary-slides="secondarySlides"
        :loading="loadingBanners"
      />
    </section>
    <LazyHomeNewProductSection />
    <find-by-brand />
    <home-banner-gallery />
    <LazyHomeFlashOfferSection />
    <promotion-gallery />
    <LazyHomeFindByCategory />
    <promotion-section />
    <payment-section />
    <save-purchase-section :show-section-small="false" />
    <section
      class="home-seo-content container py-4 py-lg-5 pb-1 pb-lg-2"
      aria-label="Sobre Digitalife"
    >
      <h1 class="home-seo-content__title">
        Digitalife: tienda de tecnología en México
      </h1>
      <p>
        En Digitalife somos una tienda de tecnología en México especializada en
        la venta de PC, Mac, computadoras gamer, computadoras para diseñadores,
        youtuber, laptops, monitores, componentes PC gamer y accesorios de
        cómputo como teclados, mouse, audífonos, bocinas e impresoras al mejor
        precio.
      </p>
      <p>
        Contamos con un amplio catálogo para quienes buscan comprar hardware en
        México, desde equipos de alto rendimiento hasta laptops baratas en
        México con excelente relación calidad-precio. Trabajamos con las mejores
        marcas del mercado para ofrecer soluciones ideales para gamers,
        creadores de contenido y uso profesional.
      </p>
      <p>
        Ya sea que quieras armar tu setup, mejorar tu rendimiento o encontrar
        los mejores monitores gaming a buen precio, en Digitalife encontrarás
        todo en un solo lugar, con envíos rápidos a todo México, garantía y
        atención especializada. Contamos con tiendas físicas en Guadalajara,
        Zapopan, Tlaquepaque, Tlajomulco y todo Jalisco, garantizando que
        compras en una empresa confiable y segura.
      </p>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.mb-swiper {
  @media (min-width: 992px) {
    margin-bottom: 80px;
  }
}

.home-seo-content {
  text-align: justify;
  color: #222;
  font-size: 1rem;
  line-height: 1.7;

  &__title {
    font-size: clamp(1.35rem, 2.2vw, 2rem);
    line-height: 1.3;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1rem;
  }
}
</style>
